import React, { useMemo } from 'react';
import { Navbar, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import UserMenu from './components/UserMenu';
import WhoAmI from './components/WhoAmI';
import HeaderLogo from '../../../../assets/images/logo_header_page.png';
import {
  cedapStatisticsRoute,
  indexRoute,
  auditiveStatistics,
  cedapBirthStatisticsRoute,
  cedapBreastfeedingStatisticsRoute,
  manageNewbornRoute,
  cedapSupportRoute,
  auditiveSupportRoute,
  pediatricCentreRoutes,
  ophthalmologyRoute,
} from '../../../../routes';
import Link from '../../../Link';
import * as userMenus from '../../../../config/userMenu';
import * as accountMenus from '../../../../config/accountMenu';
import useUser from '../../../../hooks/useUser';
import {
  ROLE_AUDITIVE_LEVEL_2,
  ROLE_GYNECOLOGIST,
  ROLE_OBSTETRICIAN,
  ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
  ROLE_REGIONAL_SUPERVISOR,
  ROLE_CEDAP_MANAGER,
  ROLE_ADMINISTRATOR,
  ROLE_NURSE_BIRTH_CENTRE,
  ROLE_NURSE_NC,
  ROLE_LHC_MANAGER,
  ROLE_AUDITIVE_SUPERVISOR,
  ROLE_SUPPORT,
  ROLE_FACILITY_MANAGER,
  PEDIATRIC_CENTRE_ROLES,
  ROLE_NEONATOLOGIST_NC,
  ROLE_NEONATOLOGIST_BIRTH_CENTRE,
  ROLE_AUDITIVE_LEVEL_3,
  ROLE_CEDAP_MANAGER_READ_ONLY,
  ROLE_OPHTHALMOLOGY_LEVEL_2,
  ROLE_OPHTHALMOLOGY_LEVEL_3,
} from '../../../../constants/roles';

import './style/header.css';

export default function Header({ showUserMenu }) {
  const { isAuth, hasRole, hasCompleteProfile, isLead, isAdmin } = useUser();

  const userMenu = useMemo(() => {
    if (!hasCompleteProfile) return {};

    if (isAdmin) return userMenus;

    if (hasRole(ROLE_SUPPORT))
      return {
        dashboard: userMenus.helpDeskDashboard,
        cedap: {
          label: 'CeDAP',
          route: cedapSupportRoute,
        },
        metabolic: {
          label: 'Screening metabolico',
          route: manageNewbornRoute,
          subMenu: userMenus.metabolic.subMenu,
        },
        auditive: {
          label: 'Screening uditivo',
          route: auditiveSupportRoute,
        },
        ophthalmology: {
          label: 'Screening oftalmologico',
          route: ophthalmologyRoute,
        },
        accountsMerge: userMenus.accountsMerge,
        manageFacilities: userMenus.manageFacilities,
        manageWorkgroups: userMenus.manageWorkgroups,
        manageUsers: userMenus.manageUsers,
        manageSupervisors: userMenus.manageSupervisors,
        manageLhcs: userMenus.manageLhcs,
        mortalityRegistry: userMenus.mortalityRegistry,
      };

    if (hasRole(ROLE_AUDITIVE_LEVEL_2))
      return { ...userMenus.auditive.subMenu, manageInvite: userMenus.manageInvite };

    if (hasRole(ROLE_AUDITIVE_LEVEL_3))
      return { ...userMenus.auditive.subMenu, manageInvite: userMenus.manageInvite };

    if (hasRole(ROLE_OPHTHALMOLOGY_LEVEL_2) || hasRole(ROLE_OPHTHALMOLOGY_LEVEL_3))
      return { ...userMenus.ophthalmology.subMenu, manageInvite: userMenus.manageInvite };

    if (hasRole([ROLE_GYNECOLOGIST, ROLE_OBSTETRICIAN]))
      return { ...userMenus.cedap.subMenu, manageInvite: userMenus.manageInvite };

    if (hasRole([ROLE_REFERENT_CONGENITAL_MALFORMATIONS])) return { ...userMenus.cedap.subMenu };

    if (hasRole([ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY]))
      return {
        ...userMenus.cedap.subMenu,
        facilitiesPerformanceStatistics: userMenus.facilitiesPerformanceStatistics,
      };

    if (hasRole(ROLE_REGIONAL_SUPERVISOR))
      return {
        manageFacilities: userMenus.manageFacilities,
        manageWorkgroups: userMenus.manageWorkgroups,
        manageUsers: userMenus.manageUsers,
        facilitiesPerformanceStatistics: userMenus.facilitiesPerformanceStatistics,
        statistics: userMenus.regionalSupervisorStatistics,
        mortalityRegistry: userMenus.mortalityRegistry,
        manageInvite: userMenus.manageInvite,
      };

    if (hasRole(ROLE_AUDITIVE_SUPERVISOR))
      return {
        hsList: userMenus.auditiveHsList,
        firstLevelStatistics: userMenus.auditiveFirstLevelStatistics,
        secondLevelStatistics: userMenus.auditiveSecondLevelStatistics,
      };

    if (hasRole([ROLE_NURSE_BIRTH_CENTRE, ROLE_NURSE_NC]))
      return {
        landing: userMenus.landing,
        cedap: userMenus.cedap,
        manageNewborn: userMenus.manageNewborn,
        metabolic: userMenus.metabolic,
        auditive: userMenus.auditive,
        ophthalmology: userMenus.ophthalmology,
      };

    if (hasRole([ROLE_LHC_MANAGER, ROLE_FACILITY_MANAGER]))
      return {
        statistics: userMenus.statisticsLanding,
        cedap: {
          label: 'CeDAP',
          subMenu: [
            {
              label: 'Statistiche',
              route: cedapStatisticsRoute,
            },
            {
              label: 'Statistiche sul parto',
              route: cedapBirthStatisticsRoute,
            },
            {
              label: 'Allattamento',
              route: cedapBreastfeedingStatisticsRoute,
            },
          ],
        },
        auditive: {
          label: 'Screening uditivo',
          route: auditiveStatistics,
        },
      };

    if (hasRole(PEDIATRIC_CENTRE_ROLES)) {
      return {
        home: {
          label: 'Home',
          route: pediatricCentreRoutes.dashboard,
        },
        sampleRequests: {
          label: 'Richiami',
          route: pediatricCentreRoutes.recallsList,
        },
        reports: {
          label: 'Verifica referti',
          route: pediatricCentreRoutes.reportList,
        },
        manageInvite: userMenus.manageInvite,
      };
    }

    if (hasRole([ROLE_NEONATOLOGIST_NC, ROLE_NEONATOLOGIST_BIRTH_CENTRE]) && !isLead) {
      const { regionalSupervisorStatistics, ...rest } = userMenus;
      return rest;
    }

    return userMenus;
  }, [hasCompleteProfile, hasRole, isLead, isAdmin]);

  const accountMenu = useMemo(() => {
    if (hasRole(ROLE_ADMINISTRATOR) || !hasCompleteProfile) {
      return {
        profile: accountMenus.userProfile,
        support: accountMenus.support,
        logout: accountMenus.logout,
      };
    }

    return accountMenus;
  }, [hasCompleteProfile, hasRole]);

  return (
    <header className="header">
      <div className="container-fluid">
        <Row>
          <Col md={6} className="header-left">
            <Navbar.Brand>
              <Link to={indexRoute}>
                <img alt="SNE" src={HeaderLogo} className="logo-image" />
              </Link>
            </Navbar.Brand>
          </Col>
          {isAuth && (
            <Col md={6} className="header-right">
              <Row>
                <Col md={12} className="whoAmI-section">
                  <WhoAmI />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="account-menu">
                  <div className="account-menu">
                    <UserMenu items={accountMenu} />
                  </div>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
        <Row>
          {isAuth && showUserMenu && (
            <Col md={12}>
              <div className="main-menu">
                <UserMenu className="main-menu" items={userMenu} />
              </div>
            </Col>
          )}
        </Row>
      </div>
    </header>
  );
}

Header.propTypes = {
  showUserMenu: PropTypes.bool,
};

Header.defaultProps = {
  showUserMenu: true,
};
